import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PermissionDenied } from '@/components/ui/permission-denied';
import { EntitySelect } from '@/components/EntitySelect';
import type { ApiError } from '@/core/api';
import { useProjects, useTimeEntries } from '../queries';
import { searchProjects } from '../select';
import { formatMinutes } from '../timeFormat';

/** A project-time report aggregated client-side over the time-entries list. */
export function ProjectsReportsPage() {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [projectId, setProjectId] = useState('');

  const { data, isPending, isError, error } = useTimeEntries({
    project: projectId ? Number(projectId) : undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });
  const { data: projectsData } = useProjects({});

  const projectNames = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of projectsData?.results ?? []) map.set(p.id, p.name);
    return map;
  }, [projectsData]);

  // Aggregate the exact stored minutes — summing derived decimal hours drifts.
  const { byProject, totalMinutes, billableMinutes } = useMemo(() => {
    const entries = data?.results ?? [];
    const byProject = new Map<number, { minutes: number; billable: number }>();
    let totalMinutes = 0;
    let billableMinutes = 0;
    for (const e of entries) {
      const m = e.minutes || 0;
      totalMinutes += m;
      if (e.billable) billableMinutes += m;
      const bucket = byProject.get(e.project) ?? { minutes: 0, billable: 0 };
      bucket.minutes += m;
      if (e.billable) bucket.billable += m;
      byProject.set(e.project, bucket);
    }
    return { byProject, totalMinutes, billableMinutes };
  }, [data]);

  const rows = useMemo(
    () =>
      [...byProject.entries()]
        .map(([projectId, agg]) => ({ projectId, ...agg }))
        .sort((a, b) => b.minutes - a.minutes),
    [byProject],
  );

  const billablePct =
    totalMinutes > 0 ? Math.round((billableMinutes / totalMinutes) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Project Reports</h1>
          <p className="text-muted-foreground text-sm">Logged time, per project.</p>
        </div>
        <Button asChild variant="outline">
          <Link to="/projects/budget">
            Budget vs Actual <ArrowRight className="size-4" />
          </Link>
        </Button>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <span className="text-muted-foreground block text-xs">Project</span>
          <div className="w-56">
            <EntitySelect
              queryKey="report-project-filter"
              value={projectId}
              onChange={setProjectId}
              search={searchProjects}
              placeholder="All projects"
            />
          </div>
        </div>
        <div className="space-y-1">
          <label htmlFor="rep-date-from" className="text-muted-foreground text-xs">From</label>
          <Input
            id="rep-date-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-44"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="rep-date-to" className="text-muted-foreground text-xs">To</label>
          <Input
            id="rep-date-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-44"
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader>
            <CardDescription>Total hours</CardDescription>
            <CardTitle className="text-3xl tabular-nums" title={`${(totalMinutes / 60).toFixed(2)}h`}>
              {formatMinutes(totalMinutes)}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>Billable hours</CardDescription>
            <CardTitle className="text-3xl tabular-nums" title={`${(billableMinutes / 60).toFixed(2)}h`}>
              {formatMinutes(billableMinutes)}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-muted-foreground text-sm">{billablePct}% of total</CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardDescription>Projects with time logged</CardDescription>
            <CardTitle className="text-3xl tabular-nums">{rows.length}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {isError ? (
        (error as unknown as ApiError)?.status === 403 ? (
          <PermissionDenied message={(error as unknown as ApiError)?.message} />
        ) : (
          <p className="text-destructive text-sm">
            {(error as unknown as ApiError)?.message ?? 'Failed to load report.'}
          </p>
        )
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead className="text-right">Hours</TableHead>
                <TableHead className="text-right">Billable</TableHead>
                <TableHead className="text-right">% Billable</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isPending ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={4}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : rows.length ? (
                rows.map((row) => (
                  <TableRow key={row.projectId}>
                    <TableCell className="font-medium">
                      {projectNames.get(row.projectId) ?? `#${row.projectId}`}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={`${(row.minutes / 60).toFixed(2)}h`}>
                      {formatMinutes(row.minutes)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={`${(row.billable / 60).toFixed(2)}h`}>
                      {formatMinutes(row.billable)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-right tabular-nums">
                      {row.minutes > 0 ? Math.round((row.billable / row.minutes) * 100) : 0}%
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-muted-foreground text-center">
                    No time logged in this range.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
